/**
 * Vincent B1011 (Eric 2026-02-26 SPEC STRICK PROD) — Modale d'import
 * CSV réutilisable pour bulk-imports (dictionnaire, tags, etc.).
 *
 * Props :
 *   - open (bool)              : contrôle l'affichage
 *   - onClose (fn)             : callback fermeture
 *   - title (string)           : titre de la modale
 *   - description (node)       : brève explication + format attendu
 *   - templateCsv (string)     : contenu CSV à télécharger comme modèle
 *   - templateFilename (string): nom du fichier téléchargé
 *   - onSubmit (fn(csvText))   : callback qui appelle l'API et retourne
 *                                 la réponse (added, errors, etc.)
 *   - resultRenderer (fn(res)) : rend le résumé personnalisé après un
 *                                 import réussi
 *   - dataTestidPrefix (str)   : préfixe des data-testid (unicité)
 *
 * La modale garde son propre state (fichier sélectionné + preview des
 * 5 premières lignes + résultat + isSubmitting). Fermable par overlay
 * ou touche Escape via le contrôle du parent.
 */
import { useEffect, useRef, useState } from "react";

export default function CsvImportModal({
  open,
  onClose,
  title,
  description,
  templateCsv,
  templateFilename,
  onSubmit,
  resultRenderer,
  dataTestidPrefix = "csv-import",
  lang = "fr",
}) {
  const [csvText, setCsvText] = useState("");
  // Vincent B1013 (Eric 2026-02-26 SPEC STRICK PROD) — Support XLSX :
  // on garde le fichier en base64 pour l'envoyer tel quel au backend
  // qui le parse via openpyxl. Pas de preview côté client.
  const [xlsxB64, setXlsxB64] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [filename, setFilename] = useState("");
  const [isXlsx, setIsXlsx] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  // Reset local state au moment de fermer ou de ré-ouvrir la modale.
  useEffect(() => {
    if (!open) {
      setCsvText("");
      setXlsxB64("");
      setPreviewRows([]);
      setFilename("");
      setIsXlsx(false);
      setResult(null);
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const _arrayBufferToBase64 = (buffer) => {
    // Chunked pour éviter le RangeError sur les grosses buffers.
    const bytes = new Uint8Array(buffer);
    let binary = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFilename(file.name);
    setResult(null);
    // Bogue 1013 — `.xls` (Excel 97-2003) n'est pas lisible par openpyxl :
    // on le dit tout de suite au lieu de laisser partir un import qui échoue.
    if (/\.xls$/i.test(file.name)) {
      setResult({ error: lang === "fr"
        ? "Format .xls (Excel 97-2003) non pris en charge : ouvre le fichier dans Excel puis « Enregistrer sous » au format .xlsx."
        : "Legacy .xls not supported: re-save as .xlsx." });
      setCsvText(""); setXlsxB64(""); setIsXlsx(false); setPreviewRows([]);
      return;
    }
    const isXlsxFile = /\.xlsx$/i.test(file.name);
    setIsXlsx(isXlsxFile);
    if (isXlsxFile) {
      const buf = await file.arrayBuffer();
      const b64 = _arrayBufferToBase64(buf);
      setXlsxB64(b64);
      setCsvText("");
      setPreviewRows([]);  // pas de preview pour XLSX (parsé côté serveur)
    } else {
      const text = await file.text();
      setCsvText(text);
      setXlsxB64("");
      // Prévisualise les 5 premières lignes (header inclus) pour valider
      // rapidement que le mapping colonnes est correct.
      const lines = text.split(/\r?\n/).filter((l) => l.trim()).slice(0, 6);
      setPreviewRows(lines.map((l) => l.split(",")));
    }
  };

  const handleDownloadTemplate = () => {
    if (!templateCsv) return;
    const blob = new Blob([templateCsv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateFilename || "modele.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const hasPayload = !!csvText.trim() || !!xlsxB64.trim();
  const handleSubmit = async () => {
    if (!hasPayload) return;
    setSubmitting(true);
    try {
      // Vincent B1013 — payload conditionnelle : csv OU xlsx_b64.
      const payload = isXlsx ? { xlsx_b64: xlsxB64 } : { csv: csvText };
      const res = await onSubmit(payload);
      setResult(res);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e?.message || "Erreur inconnue" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid={`${dataTestidPrefix}-modal-overlay`}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby={`${dataTestidPrefix}-modal-title`}
        data-testid={`${dataTestidPrefix}-modal`}
      >
        <header className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h2
            id={`${dataTestidPrefix}-modal-title`}
            className="text-lg font-semibold text-zinc-900"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900 text-xl leading-none"
            aria-label={lang === "fr" ? "Fermer" : "Close"}
            data-testid={`${dataTestidPrefix}-modal-close`}
          >
            ×
          </button>
        </header>
        <div className="px-5 py-4 space-y-4">
          {description && (
            <div className="text-sm text-zinc-700" data-testid={`${dataTestidPrefix}-description`}>
              {description}
            </div>
          )}
          {templateCsv && (
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="text-xs font-semibold px-2 py-1 rounded bg-sky-50 border border-sky-300 text-sky-800 hover:bg-sky-100"
              data-testid={`${dataTestidPrefix}-download-template`}
            >
              {lang === "fr" ? "Télécharger un modèle CSV" : "Download CSV template"}
            </button>
          )}
          <div>
            <label
              className="block text-xs font-semibold text-zinc-700 mb-1"
              htmlFor={`${dataTestidPrefix}-file-input`}
            >
              {/* Bogue 1013 (Éric 2026-06-25) — L'import Excel existait mais rien
                  ne le disait : libellés, bouton et champ parlaient tous de CSV,
                  donc Éric convertissait encore ses fichiers à la main. */}
              {lang === "fr" ? "Fichier CSV ou Excel (.xlsx)" : "CSV or Excel (.xlsx) file"}
            </label>
            {/* Tâche 1437 (Eric 2026-10-02) — Le champ de fichier NATIF
                affichait « Choisir un fichier / Aucun fichier choisi » en
                français au milieu d'un écran anglais. Ces deux libellés sont
                fabriqués par le NAVIGATEUR : ils suivent sa langue et aucun
                appel au traducteur ne peut les atteindre. Seule issue : cacher
                l'input et poser notre propre bouton.
                ⚠️ L'input reste HORS du bouton (motif B774 v2) : un
                `<label>` qui contient un input de type file casse le
                glisser-déposer sur Chrome et Firefox. */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50"
                data-testid={`${dataTestidPrefix}-file-pick`}
              >
                {lang === "fr" ? "Choisir un fichier" : "Choose a file"}
              </button>
              <span
                className="text-xs text-zinc-500 truncate max-w-[16rem]"
                data-testid={`${dataTestidPrefix}-file-name`}
              >
                {filename || (lang === "fr" ? "Aucun fichier choisi" : "No file chosen")}
              </span>
            </div>
            <input
              id={`${dataTestidPrefix}-file-input`}
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid={`${dataTestidPrefix}-file-input`}
            />
            {filename && (
              <p className="text-[11px] text-zinc-500 mt-1 font-mono" data-testid={`${dataTestidPrefix}-filename`}>
                {filename}
                {isXlsx && (
                  <span className="ml-2 text-emerald-700 not-italic font-semibold" data-testid={`${dataTestidPrefix}-xlsx-badge`}>
                    · XLSX
                  </span>
                )}
              </p>
            )}
            {isXlsx && (
              <p className="text-[11px] text-amber-800 mt-1" data-testid={`${dataTestidPrefix}-xlsx-notice`}>
                {lang === "fr"
                  ? "Aperçu XLSX non disponible : le fichier sera converti en CSV côté serveur au moment de l'import."
                  : "XLSX preview unavailable: the file will be converted to CSV server-side at import time."}
              </p>
            )}
          </div>
          {previewRows.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Aperçu (5 premières lignes)" : "Preview (first 5 rows)"}
              </p>
              <div className="overflow-x-auto border border-zinc-200 rounded text-xs" data-testid={`${dataTestidPrefix}-preview`}>
                <table className="min-w-full">
                  <tbody>
                    {previewRows.map((cells, idx) => (
                      <tr key={idx} className={idx === 0 ? "bg-zinc-50 font-semibold" : ""}>
                        {cells.map((c, ci) => (
                          <td key={ci} className="px-2 py-1 border-b border-zinc-100 truncate max-w-xs">
                            {c}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {result && !result.error && (
            <div
              className="bg-emerald-50 border border-emerald-300 rounded p-3 text-sm text-emerald-900"
              data-testid={`${dataTestidPrefix}-result-success`}
              role="status"
            >
              {resultRenderer ? resultRenderer(result) : (
                <>
                  <p className="font-semibold">
                    {lang === "fr" ? "Import terminé." : "Import complete."}
                  </p>
                  <p className="font-mono text-xs mt-1">{JSON.stringify(result)}</p>
                </>
              )}
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs font-semibold">
                    {lang === "fr" ? `${result.errors.length} avertissement(s)` : `${result.errors.length} warning(s)`}
                  </summary>
                  <ul className="mt-1 text-xs list-disc pl-5" data-testid={`${dataTestidPrefix}-result-errors`}>
                    {result.errors.map((err, i) => (
                      <li key={i} className="font-mono">{err}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
          {result && result.error && (
            <div
              className="bg-red-50 border border-red-300 rounded p-3 text-sm text-red-900"
              data-testid={`${dataTestidPrefix}-result-error`}
              role="alert"
            >
              {lang === "fr" ? "Erreur : " : "Error: "}{result.error}
            </div>
          )}
        </div>
        <footer className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
            data-testid={`${dataTestidPrefix}-cancel`}
          >
            {lang === "fr" ? (result ? "Fermer" : "Annuler") : (result ? "Close" : "Cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !hasPayload || !!result}
            className="text-xs font-semibold px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid={`${dataTestidPrefix}-submit`}
          >
            {submitting
              ? (lang === "fr" ? "Import…" : "Importing…")
              : (lang === "fr" ? "Importer" : "Import")}
          </button>
        </footer>
      </div>
    </div>
  );
}
